import React from 'react';
import PropTypes from 'prop-types';
import useDataGrid from '@afosto/utils-react/datagrid/useDataGrid';
import useIntl from '@afosto/utils-react/intl/useIntl';
import usePermissions from '@afosto/utils-react/permissions/usePermissions';
import { FormattedMessage } from 'react-intl';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import Hidden from '@material-ui/core/Hidden';
import CalculationListItem from '../CalculationListItem';
import DataGrid from '@afosto/app-components-react/DataGrid';
import DataList from '@afosto/app-components-react/DataList';
import { actionsFormatter, calculationFormatter } from './formatters';
import translations from './translations';

const CalculationsSection = ({
  calculations,
  onCreate,
  onCreateManually,
  showActions,
  showCreate,
  showCreateAutomatically,
  showCreateManually,
  showOrderCalculationColumns,
}) => {
  const intl = useIntl();
  const [hasPermissions, UnauthorizedComponent] = usePermissions(['odr:orders:read']);

  const dataSource = useDataGrid(calculations, {
    modules: ['dataProperties'],
    dataProperties: {
      fixedProperties: [
        {
          key: 'number',
          name: intl.formatMessage({
            id: 'dataGrid.columns.calculations.number',
            defaultMessage: 'Number',
            description: 'The column name for column number',
          }),
          type: 'calculation',
        },
        {
          key: 'total',
          name: intl.formatMessage({
            id: 'dataGrid.columns.calculations.total',
            defaultMessage: 'Total',
            description: 'The column name for column total',
          }),
          type: 'currency',
        },
        ...(showOrderCalculationColumns
          ? [
              {
                key: 'is_valid',
                name: intl.formatMessage({
                  id: 'dataGrid.columns.calculations.isValid',
                  defaultMessage: 'Valid',
                  description: 'The column name for column is valid',
                }),
                type: 'boolean',
              },
              {
                key: 'is_accepted',
                name: intl.formatMessage({
                  id: 'dataGrid.columns.calculations.isAccepted',
                  defaultMessage: 'Accepted',
                  description: 'The column name for column is accepted',
                }),
                type: 'boolean',
              },
              {
                key: 'is_confirmed',
                name: intl.formatMessage({
                  id: 'dataGrid.columns.calculations.isConfirmed',
                  defaultMessage: 'Confirmed',
                  description: 'The column name for column is confirmed',
                }),
                type: 'boolean',
              },
            ]
          : []),
        {
          key: 'created_at',
          name: intl.formatMessage({
            id: 'dataGrid.columns.calculations.createdAt',
            defaultMessage: 'Created at',
            description: 'The column name for column created at',
          }),
          type: 'datetime',
        },
        ...(showActions
          ? [
              {
                key: 'actions',
                name: '',
                type: 'actions',
              },
            ]
          : []),
      ],
    },
  });

  if (!hasPermissions()) {
    return (
      <Card>
        <CardHeader
          title={intl.formatMessage(translations.title)}
          titleTypographyProps={{ variant: 'h6' }}
        />
        <CardContent>
          <UnauthorizedComponent showActions={false} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader
        title={intl.formatMessage(translations.title)}
        titleTypographyProps={{ variant: 'h6' }}
        action={
          showCreate &&
          hasPermissions(['odr:orders:read']) && (
            <>
              {showCreateManually && (
                <Button
                  color="default"
                  variant="outlined"
                  onClick={onCreateManually}
                  style={{ marginRight: 8 }}
                >
                  <FormattedMessage {...translations.actions.addManually.label} />
                </Button>
              )}
              {showCreateAutomatically && (
                <Button color="primary" variant="outlined" onClick={onCreate}>
                  <FormattedMessage {...translations.actions.add.label} />
                </Button>
              )}
            </>
          )
        }
      />
      <Hidden implementation="js" mdUp>
        <DataList
          dataSource={dataSource}
          renderItem={item => <CalculationListItem calculation={item} key={item.id} />}
        />
      </Hidden>
      <Hidden implementation="js" smDown>
        <DataGrid
          dataSource={dataSource}
          formatters={{ actions: actionsFormatter, calculation: calculationFormatter }}
        />
      </Hidden>
    </Card>
  );
};

CalculationsSection.propTypes = {
  calculations: PropTypes.array,
  onCreate: PropTypes.func,
  onCreateManually: PropTypes.func,
  showActions: PropTypes.bool,
  showCreate: PropTypes.bool,
  showCreateAutomatically: PropTypes.bool,
  showCreateManually: PropTypes.bool,
  showOrderCalculationColumns: PropTypes.bool,
};

CalculationsSection.defaultProps = {
  calculations: [],
  onCreate: undefined,
  onCreateManually: undefined,
  showActions: false,
  showCreate: true,
  showCreateAutomatically: true,
  showCreateManually: false,
  showOrderCalculationColumns: false,
};

export default CalculationsSection;
